import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { CartService } from '../../../Cart/types';
import CartItemOptionAdapter from '../../../Cart/CartItemOptionAdapter/CartItemOptionAdapter';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import BaggageIcon from '../../../Cart/BaggageIcon/BaggageIcon';
import { removeDuplicates } from '@shared//lib/removeDuplicates';
import { useAggregatedSummaryServices } from '../lib/useAggregatedSummaryServices';
import { isShowInsurancesByPassengers } from '../../../ServiceSummary/utils';
import { CdnIcon } from '@shared/ui';
const HEADER_ICON_MAP = {
    Seat: 'Services/Seat Selection',
    Meal: 'Services/Meal',
    Animal: 'Baggage/Pet in cargo'
};
const CART_SERVICE_MAP = {
    Seat: CartService.Seats,
    Baggage: CartService.Baggage,
    Animal: CartService.Baggage,
    Meal: CartService.Meal,
    BusinessLounge: CartService.BusinessLounge,
    Insurance: CartService.Insurances,
    Aeroexpress: CartService.Aeroexpress,
    Sms: CartService.SMS,
    Misc: CartService.Misc,
    Transfer: CartService.Transfer
};
const ServiceSummary = ({ children, renderSummary, serviceType, filter, onDelete, mode = 'default' }) => {
    var _a, _b;
    const aggregatedSummaryServices = useAggregatedSummaryServices();
    const summaryGroupsByType = (_a = aggregatedSummaryServices === null || aggregatedSummaryServices === void 0 ? void 0 : aggregatedSummaryServices[serviceType]) !== null && _a !== void 0 ? _a : [];
    const { isBaggageService, isInsuranceService } = useMemo(() => ({
        isBaggageService: serviceType === 'Baggage' || serviceType === 'Animal',
        isInsuranceService: serviceType === 'Insurance'
    }), [serviceType]);
    const onDeleteHandler = useCallback((deleteMeta) => {
        onDelete === null || onDelete === void 0 ? void 0 : onDelete(CART_SERVICE_MAP[serviceType], deleteMeta);
    }, [onDelete, serviceType]);
    const processItems = useCallback((items = []) => {
        let processedItems = [...items];
        if (filter) {
            processedItems = processedItems.filter(item => filter.values.includes(item[filter.param]));
        }
        if (isInsuranceService) {
            processedItems = isShowInsurancesByPassengers(mode)
                ? removeDuplicates(processedItems, item => `${item.id}/${item.passengerName}`)
                : removeDuplicates(processedItems);
        }
        return processedItems.map(item => {
            let icon = null;
            if (isBaggageService && (item.iconName || item.rfisc)) {
                icon = React.createElement(BaggageIcon, { baggageType: item.iconName, rfisc: item.rfisc });
            }
            else if (isInsuranceService) {
                icon = React.createElement(CdnIcon, { icon: 'Services/Transfer' });
            }
            return Object.assign(Object.assign({}, item), { icon, onDelete: onDelete && item.deleteMeta ? () => onDeleteHandler(item.deleteMeta) : null });
        });
    }, [mode, filter, isBaggageService, isInsuranceService, onDelete, onDeleteHandler]);
    const groups = useMemo(() => {
        return summaryGroupsByType
            .map(group => (Object.assign(Object.assign({}, group), { headerIcon: HEADER_ICON_MAP[serviceType] ? React.createElement(CdnIcon, { icon: HEADER_ICON_MAP[serviceType] }) : undefined, items: processItems(group.items) })))
            .filter(group => { var _a; return (_a = group.items) === null || _a === void 0 ? void 0 : _a.length; });
    }, [serviceType, summaryGroupsByType, processItems]);
    if (renderSummary) {
        return renderSummary(groups);
    }
    const content = useMemo(() => {
        if (!groups.length) {
            return null;
        }
        return (React.createElement(ServiceSummaryDivider, null, groups.map((group, index) => {
            var _a, _b;
            const directionId = group.allSegments ? 'allSegments' : (_a = group.direction) === null || _a === void 0 ? void 0 : _a.id;
            const travellerId = (_b = group.traveller) === null || _b === void 0 ? void 0 : _b.id;
            const key = directionId && travellerId ? `${directionId}/${travellerId}` : index;
            return React.createElement(CartItemOptionAdapter, { key: key, option: group });
        })));
    }, [groups]);
    return (_b = children === null || children === void 0 ? void 0 : children(content)) !== null && _b !== void 0 ? _b : null;
};
export default React.memo(ServiceSummary);
